// import { HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
// import { inject, PLATFORM_ID } from '@angular/core';
// import { isPlatformBrowser } from '@angular/common';
// import { Observable, from } from 'rxjs';
// import { switchMap } from 'rxjs/operators';
// import { CryptoServiceService } from './crypto-service.service'; // Update path as needed

// export const encryptionInterceptor = (
//   req: HttpRequest<any>,
//   next: HttpHandlerFn
// ): Observable<HttpEvent<any>> => {
//   const cryptoService = inject(CryptoServiceService);
//   const platformId = inject(PLATFORM_ID);

//   // SSR check
//   if (!isPlatformBrowser(platformId)) {
//     // Skip encryption on server
//     return next(req);
//   }

//   const url = new URL(req.urlWithParams, window.location.origin);
//   const searchParams = new URLSearchParams(url.search);
//   const paramPairs: string[] = [];
//   const UNENCODED_COMMA_KEYS = [
//     'institutionNameId',
//     'boardNameId',
//     'followUpStatusId',
//     'intakeId',
//     'rmGroupId',
//     'srmGroupId',
//     'urmGroupId',
//     'caseOwner',
//     'universityCountry',
//     'globalCountry',
//     'pathwayUniversity',
//     'from',
//     'type',
//     'email'
//   ]; 
//   const keysToEncrypt = [
//     'id',
//     'Id',
//     'universityId',
//     'courseId',
//     'userId',
//     'roleId',
//     'smtpsId',
//     'migrationId',
//     'applicationId',
//     'messageId',
//     'mailCommunicationId',
//     'userMailCalendarId',
//     'bankDetailsId',
//     'voucherId',
//     'agentsId',
//     'agentId',
//     'universityEntryRequirementId',
//     'universityFilterId',
//     'universityAffiliateId',
//     'universityContractId',
//     'emailTemplatesId',
//     'primaryStatusId',
//     'agentCommunicationStatusId',
//     'studentPreviousQualificationId',
//     'applicationCommentsId',
//     'username',
//     'jobRole',
//     'storeId',
//     'manufactureId',
//     'camsId',
//     'universityCountryId',
//     'destinationCountryId',
//     'studentCountryId',
//     'intakeName',
//     'promotion',
//     'university',
//     'userName'
//   ];

//   const encryptPromises: Promise<void>[] = [];

//   searchParams.forEach((value, key) => {
//     if (keysToEncrypt.includes(key) && value) {
//       encryptPromises.push(
//         cryptoService.encrypt(value).then((encrypted) => {
//           paramPairs.push(`${key}=${encrypted}`);
//         })
//       );
//     } else {
//       // paramPairs.push(`${key}=${encodeURIComponent(value)}`);
//       let finalValue = value;

//         if (UNENCODED_COMMA_KEYS.includes(key) && value) {
//             if (value.includes('%2C')) {
//                 finalValue = decodeURIComponent(value);
//             }
//             let encodedValue = encodeURIComponent(finalValue);
//             encodedValue = encodedValue.replace(/%2C/g, ',');
//             encodedValue = encodedValue.replace(/%40/g, '@');
//             paramPairs.push(`${key}=${encodedValue}`);

//         } else {
//             paramPairs.push(`${key}=${encodeURIComponent(finalValue)}`);
//         }
//     }
//   });

//   return from(Promise.all(encryptPromises)).pipe(
//     switchMap(() => {
//       const updatedUrl =
//         url.origin +
//         url.pathname +
//         (paramPairs.length ? '?' + paramPairs.join('&') : '');
//       const cloned = req.clone({ url: updatedUrl });
//       return next(cloned);
//     })
//   );
// };
import {
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpParams,
} from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CryptoServiceService } from './crypto-service.service';
import { SKIP_QUERY_ENCRYPTION } from '../../../interceptors/skip-encryption-context';

export const encryptionInterceptor = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {

  const cryptoService = inject(CryptoServiceService);
  const platformId = inject(PLATFORM_ID);

  // ===============================
  // SSR CHECK
  // ===============================
  if (!isPlatformBrowser(platformId)) {
    return next(req);
  }

  // ===============================
  // 🔹 STEP 1: ADD userId TO BODY
  // ===============================

  let modifiedReq = req;

  try {
    const currentUser = JSON.parse(
      localStorage.getItem('currentUser') || '{}'
    );

    const userId = currentUser?.id;

    if (userId) {

      // ✅ If JSON body
      if (req.body && !(req.body instanceof FormData)) {
        modifiedReq = req.clone({
          body: {
            ...req.body,
            userId: userId
          }
        });
      }

      // ✅ If FormData
      if (req.body instanceof FormData) {
        req.body.append('userId', userId);
      }
    }

  } catch (error) {
    console.warn('Unable to append userId to request body');
  }

  if (modifiedReq.context.get(SKIP_QUERY_ENCRYPTION)) {
    return next(modifiedReq);
  }

  // ===============================
  // 🔹 STEP 2: ENCRYPT QUERY PARAMS
  // ===============================

  const url = new URL(modifiedReq.urlWithParams, window.location.origin);
  const searchParams = new URLSearchParams(url.search);
  const paramPairs: string[] = [];

  const UNENCODED_COMMA_KEYS = [
    'institutionNameId',
    'boardNameId',
    'followUpStatusId',
    'intakeId',
    'rmGroupId',
    'srmGroupId',
    'urmGroupId',
    'caseOwner',
    'universityCountry',
    'globalCountry',
    'pathwayUniversity',
    'from',
    'type',
    'email'
  ];

  const keysToEncrypt = [
    'id',
    'Id',
    'universityId',
    'courseId',
    'userId',
    'roleId',
    'smtpsId',
    'migrationId',
    'applicationId',
    'messageId',
    'mailCommunicationId',
    'userMailCalendarId',
    'bankDetailsId',
    'voucherId',
    'agentsId',
    'agentId',
    'universityEntryRequirementId',
    'universityFilterId',
    'universityAffiliateId',
    'universityContractId',
    'emailTemplatesId',
    'primaryStatusId',
    'agentCommunicationStatusId',
    'studentPreviousQualificationId',
    'applicationCommentsId',
    'username',
    'jobRole',
    'storeId',
    'manufactureId',
    'camsId',
    'universityCountryId',
    'destinationCountryId',
    'studentCountryId',
    'intakeName',
    'promotion',
    'university',
    'userName',
    //'viewUserId'
  ];

  const encryptPromises: Promise<void>[] = [];

  searchParams.forEach((value, key) => {

    if (keysToEncrypt.includes(key) && value) {
      encryptPromises.push(
        cryptoService.encrypt(value).then((encrypted) => {
          paramPairs.push(`${key}=${encrypted}`);
        })
      );
    } else {

      let finalValue = value;

      if (UNENCODED_COMMA_KEYS.includes(key) && value) {

        if (value.includes('%2C')) {
          finalValue = decodeURIComponent(value);
        }

        let encodedValue = encodeURIComponent(finalValue);
        encodedValue = encodedValue.replace(/%2C/g, ',');
        encodedValue = encodedValue.replace(/%40/g, '@');

        paramPairs.push(`${key}=${encodedValue}`);

      } else {
        paramPairs.push(`${key}=${encodeURIComponent(finalValue)}`);
      }
    }
  });

  // ===============================
  // 🔹 STEP 3: FINAL REQUEST
  // ===============================

  return from(Promise.all(encryptPromises)).pipe(
    switchMap(() => {

      const updatedUrl =
        url.origin +
        url.pathname +
        (paramPairs.length ? '?' + paramPairs.join('&') : '');

      // Query values are already inlined on updatedUrl via urlWithParams.
      // Leaving the original HttpParams on the clone makes Angular append
      // them a second time (e.g. ?q=1000&tenantId=1&q=1000&tenantId=1).
      const finalReq = modifiedReq.clone({
        url: updatedUrl,
        params: new HttpParams(),
      });

      return next(finalReq);
    })
  );
};
